import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import * as v from "valibot";
import {
  AdminAccountSchema,
  AdminCallSchema,
  AdminGuestPassSchema,
  AdminCallLinkSchema,
  AdminReportSchema,
  AdminAuditSchema,
  AuthenticatedSnapshotSchema,
  ReportStatusSchema,
  AnswerTextSchema,
  AvatarSchema,
  AvailabilitySchema,
  CallLinkIdSchema,
  CallLinkSchema,
  CallMediaSchema,
  CallViewSchema,
  ChatMessageSchema,
  GroupLinkIdSchema,
  GroupLinkSchema,
  GroupInvitationSchema,
  IdSchema,
  IceCandidateSchema,
  IceSignalSchema,
  LocaleSchema,
  moderationActions,
  OfferTextSchema,
  PersonSchema,
  ProfileCodeSchema,
  PushSubscriptionSchema,
  RevisionSchema,
} from "./schemas";

const invalid = "بيانات غير صحيحة";
const nameMessage =
  "أدخل اسمًا من حرفين على الأقل. اسم المستخدم اختياري ويتكون من ٣ إلى ٢٤ حرفًا إنجليزيًا أو رقمًا أو شرطة سفلية.";
const renameMessage =
  "أدخل اسمًا من حرفين على الأقل واسم مستخدم من ٣ إلى ٢٤ حرفًا إنجليزيًا أو رقمًا أو شرطة سفلية.";
export const guestNameMessage = "أدخل اسمًا من حرفين على الأقل.";
export const groupCallsUnavailable =
  "المكالمات الجماعية غير متاحة حاليًا. حاول لاحقًا. مكالماتك العادية تعمل كالمعتاد.";
export const groupLinkEnded = "انتهت هذه المكالمة الجماعية.";
export const groupCallFull = "هذه المكالمة الجماعية ممتلئة. حاول لاحقًا.";
export const groupCallOrganizerOnly = "هذا الإجراء لمنظّم المكالمة الجماعية فقط.";
export const groupCallRemoved = "أزالك المنظّم من هذه المكالمة الجماعية.";
export const groupCallRejected =
  "لم يوافق المنظّم على انضمامك إلى هذه المكالمة الجماعية.";
export const maxGroupCallPeople = 12;
export const usernameCooldownMs = 86400000;
export const reportCooldownMs = 5 * 60 * 1000;
const text = (max: number) =>
  v.pipe(v.string(invalid), v.maxLength(max, invalid));
const displayName = (message: string) =>
  v.pipe(
    text(40),
    v.trim(),
    v.transform((name) => name.replace(/\s+/g, " ")),
    v.minLength(2, message),
  );
// Left empty, the server picks a username.
const optionalUsername = v.optional(
  v.pipe(
    v.string(nameMessage),
    v.trim(),
    v.toLowerCase(),
    v.regex(/^(?:[a-z0-9_]{3,24})?$/, nameMessage),
  ),
);
export const ActionEnvelopeSchema = v.object(
  { action: text(30) },
  "طلب غير صحيح",
);
const ConnectionActionSchema = v.picklist([
  "request",
  "accept",
  "decline",
  "cancel-request",
  "remove-friend",
  "block",
  "unblock",
]);
export const StartCallSchema = v.object({
  offer: OfferTextSchema,
  media: v.optional(CallMediaSchema, "audio"),
  supportsScreenSharing: v.optional(v.boolean(invalid), false),
});
const reason = v.pipe(text(1000), v.trim(), v.minLength(1, "اكتب سببًا أولًا"));
const base64url = (max: number) =>
  v.pipe(text(max), v.regex(/^[\w-]+$/, invalid));
// WebAuthn allows a credential id of up to 1023 bytes.
const PasskeyIdSchema = base64url(1400);
const passkeyCredential = {
  id: PasskeyIdSchema,
  rawId: PasskeyIdSchema,
  type: v.literal("public-key", invalid),
  clientExtensionResults: v.optional(v.object({}), {}),
};
const clientDataJSON = base64url(4000);
// One answer for every failure, so a guess learns nothing about a code.
export const deviceCodeProblem = "هذا الرمز غير صالح أو انتهت مدته.";
// Crockford base32: typing a letter that looks like a digit still reaches it.
const DeviceCodeSchema = v.pipe(
  v.string(deviceCodeProblem),
  v.maxLength(30, deviceCodeProblem),
  v.transform((code) =>
    code
      .toUpperCase()
      .replace(/[\s-]/g, "")
      .replace(/O/g, "0")
      .replace(/[IL]/g, "1"),
  ),
  v.regex(/^[0-9A-HJKMNP-TV-Z]{8}$/, deviceCodeProblem),
);
const sessionToken = /^[A-Za-z0-9_-]{43}$/;
export const ApiRequestSchema = v.variant(
  "action",
  [
    v.object({
      action: v.literal("register"),
      name: displayName(nameMessage),
      username: optionalUsername,
      // Protection is optional by configuration; its verifier owns the 403 errors.
      turnstileToken: v.optional(v.unknown()),
    }),
    v.object({
      action: v.literal("restore"),
      code: v.pipe(
        text(60),
        v.trim(),
        v.regex(/^[A-Za-z0-9_-]{43}$/, "رمز الاستعادة غير صحيح"),
      ),
    }),
    v.object({ action: v.literal("passkey-register-options") }),
    v.object({
      action: v.literal("passkey-register"),
      credential: v.object({
        ...passkeyCredential,
        response: v.object({
          clientDataJSON,
          attestationObject: base64url(20000),
          transports: v.optional(
            v.pipe(v.array(text(20)), v.maxLength(10, invalid)),
          ),
        }),
      }),
    }),
    v.object({ action: v.literal("passkey-sign-in-options") }),
    v.object({
      action: v.literal("passkey-sign-in"),
      credential: v.object({
        ...passkeyCredential,
        response: v.object({
          clientDataJSON,
          authenticatorData: base64url(4000),
          signature: base64url(8000),
        }),
      }),
    }),
    v.object({ action: v.literal("passkey-delete"), id: PasskeyIdSchema }),
    v.object({ action: v.literal("device-sign-in-start") }),
    v.object({
      action: v.literal("device-sign-in-check"),
      token: v.pipe(text(60), v.regex(sessionToken, invalid)),
    }),
    v.object({
      action: v.literal("device-sign-in-review"),
      code: DeviceCodeSchema,
    }),
    v.object({
      action: v.literal("device-sign-in-approve"),
      code: DeviceCodeSchema,
    }),
    v.object({ action: v.literal("logout") }),
    v.object({ action: v.literal("delete-account") }),
    v.object({ action: v.literal("sessions") }),
    v.object({
      action: v.literal("session-end"),
      id: v.pipe(text(64), v.regex(/^[0-9a-f]{64}$/, invalid)),
    }),
    v.object({ action: v.literal("session-end-others") }),
    v.object({
      action: v.literal("report"),
      peer: IdSchema,
      reason,
      confirmed: v.literal(true, "أكد الإجراء أولًا"),
    }),
    v.object({ action: v.literal("create-recovery") }),
    v.object({ action: v.literal("status"), status: AvailabilitySchema }),
    v.object({
      action: v.literal("last-active"),
      showLastActive: v.boolean(invalid),
    }),
    v.object({
      action: v.literal("discoverable"),
      discoverable: v.boolean(invalid),
    }),
    // Shares the account's profile link, or replaces it so the old one stops working.
    v.object({
      action: v.literal("profile-link"),
      reset: v.optional(v.boolean(invalid), false),
    }),
    v.object({
      action: v.literal("profile"),
      name: displayName(renameMessage),
      username: v.pipe(
        v.string(renameMessage),
        v.trim(),
        v.toLowerCase(),
        v.regex(/^[a-z0-9_]{3,24}$/, renameMessage),
      ),
      bio: v.pipe(text(100), v.trim()),
    }),
    v.object({
      action: v.literal("avatar"),
      avatar: v.nullable(AvatarSchema),
    }),
    v.object({ action: v.literal("sync") }),
    // Call notifications for this signed-in session's device.
    v.object({
      action: v.literal("push-subscribe"),
      subscription: PushSubscriptionSchema,
      lang: LocaleSchema,
    }),
    v.object({
      action: v.literal("push-renew"),
      previousEndpoint: PushSubscriptionSchema.entries.endpoint,
      subscription: PushSubscriptionSchema,
    }),
    v.object({ action: v.literal("push-unsubscribe") }),
    v.object({ action: v.literal("create-link") }),
    v.object({ action: v.literal("cancel-link") }),
    // Visitors without an account join as guests with a name and verification.
    v.object({
      action: v.literal("join-link"),
      link: CallLinkIdSchema,
      name: v.optional(displayName(guestNameMessage)),
      turnstileToken: v.optional(v.unknown()),
    }),
    v.object({ action: v.literal("group-start") }),
    v.object({
      action: v.literal("group-invite"),
      link: GroupLinkIdSchema,
      peers: v.pipe(
        v.array(IdSchema),
        v.minLength(1, invalid),
        v.maxLength(11, invalid),
        v.check((peers) => new Set(peers).size === peers.length, invalid),
      ),
    }),
    v.object({
      action: v.literal("group-dismiss-invitation"),
      link: GroupLinkIdSchema,
    }),
    // Visitors without an account become one with a name and verification.
    v.object({
      action: v.literal("group-join"),
      link: GroupLinkIdSchema,
      name: v.optional(displayName(guestNameMessage)),
      turnstileToken: v.optional(v.unknown()),
    }),
    v.object({
      action: v.literal("group-admission"),
      link: GroupLinkIdSchema,
      enabled: v.boolean(),
    }),
    v.object({
      action: v.literal("group-approve"),
      link: GroupLinkIdSchema,
      peer: IdSchema,
    }),
    v.object({
      action: v.literal("group-reject"),
      link: GroupLinkIdSchema,
      peer: IdSchema,
    }),
    v.object({ action: v.literal("group-end"), link: GroupLinkIdSchema }),
    v.object({
      action: v.literal("group-mute"),
      link: GroupLinkIdSchema,
      peer: IdSchema,
    }),
    v.object({
      action: v.literal("group-remove"),
      link: GroupLinkIdSchema,
      peer: IdSchema,
    }),
    // A guest keeps in touch: an account under the name they joined with
    // unless they change it. Adding the host is a separate friend request.
    v.object({
      action: v.literal("keep"),
      name: v.optional(displayName(nameMessage)),
      username: optionalUsername,
    }),
    v.object({ action: v.literal("remove-recent"), callId: IdSchema }),
    // A profile link's code lets a request reach someone search does not find.
    v.object({
      action: ConnectionActionSchema,
      peer: IdSchema,
      code: v.optional(ProfileCodeSchema),
    }),
    v.object({ action: v.literal("read"), peer: IdSchema }),
    v.object({
      action: v.literal("message"),
      // Chosen by the sender, so trying again can't store a second copy.
      id: v.pipe(v.string(invalid), v.uuid(invalid)),
      peer: IdSchema,
      body: v.pipe(text(2000), v.trim(), v.minLength(1, "اكتب رسالتك أولًا")),
    }),
    v.object({
      action: v.literal("call"),
      peer: IdSchema,
      ...StartCallSchema.entries,
    }),
    v.object({
      action: v.literal("answer"),
      callId: IdSchema,
      answer: AnswerTextSchema,
    }),
    v.object({ action: v.literal("end"), callId: IdSchema }),
    v.object({
      action: v.literal("restart"),
      callId: IdSchema,
      revision: RevisionSchema,
    }),
    v.object({
      action: v.literal("restart-offer"),
      callId: IdSchema,
      revision: RevisionSchema,
      offer: OfferTextSchema,
    }),
    v.object({
      action: v.literal("restart-answer"),
      callId: IdSchema,
      revision: RevisionSchema,
      answer: AnswerTextSchema,
    }),
    v.object({
      action: v.literal("ice"),
      callId: IdSchema,
      candidate: IceCandidateSchema,
      revision: v.optional(RevisionSchema, 0),
    }),
  ],
  "طلب غير معروف",
);
// Admin writes never belong to the public action contract.
export const AdminRequestSchema = v.variant(
  "action",
  [
    v.object({
      action: v.literal("end-call"),
      callId: IdSchema,
      reason,
      confirmed: v.literal(true, "أكد الإجراء أولًا"),
    }),
    v.object({
      action: v.literal("revoke-guest-pass"),
      passId: IdSchema,
      reason,
      confirmed: v.literal(true, "أكد الإجراء أولًا"),
    }),
    v.object({
      action: v.literal("invalidate-call-link"),
      linkId: CallLinkIdSchema,
      reason,
      confirmed: v.literal(true, "أكد الإجراء أولًا"),
    }),
    v.object({
      action: v.picklist(["suspend-account", "restore-account"]),
      target: IdSchema,
      reason,
      confirmed: v.literal(true, "أكد الإجراء أولًا"),
      reportId: v.optional(IdSchema),
    }),
    v.object({
      action: v.picklist(["reset-profile", "end-sessions", "delete-account"]),
      target: IdSchema,
      reason,
      confirmed: v.literal(true, "أكد الإجراء أولًا"),
    }),
    v.object({
      action: v.picklist(["resolve-report", "dismiss-report"]),
      reportId: IdSchema,
      reason,
      confirmed: v.literal(true, "أكد الإجراء أولًا"),
    }),
  ],
  "طلب غير معروف",
);
export type AdminRequest = v.InferOutput<typeof AdminRequestSchema>;
export const AppQuerySchema = v.object({
  // A whole username, with or without its @.
  q: v.optional(
    v.pipe(
      v.string(),
      v.trim(),
      v.toLowerCase(),
      v.transform((query) => query.replace(/^@/, "").slice(0, 50)),
    ),
  ),
  profile: v.optional(ProfileCodeSchema),
  messages: v.optional(IdSchema),
  // The id of the oldest message already read; the page ends just before it.
  before: v.optional(IdSchema),
  call: v.optional(IdSchema),
  link: v.optional(CallLinkIdSchema),
  group: v.optional(GroupLinkIdSchema),
  groupInvitations: v.optional(v.literal("1")),
});
export const IceQuerySchema = v.object({ peer: IdSchema });
// A page of the admin panel's account list, as React-admin asks for it.
const pageNumber = (max: number) =>
  v.pipe(
    v.string(invalid),
    v.toNumber(invalid),
    v.integer(invalid),
    v.minValue(1, invalid),
    v.maxValue(max, invalid),
  );
export const AdminAccountsQuerySchema = v.object({
  page: v.optional(pageNumber(100000), "1"),
  perPage: v.optional(pageNumber(100), "25"),
  sort: v.optional(
    v.picklist(["created_at", "name", "username"], invalid),
    "created_at",
  ),
  order: v.optional(v.picklist(["ASC", "DESC"], invalid), "DESC"),
  // Part of a name or username, with or without its @, or a whole account id.
  q: v.optional(
    v.pipe(
      text(80),
      v.trim(),
      v.transform((query) => query.replace(/^@/, "")),
    ),
    "",
  ),
  status: v.optional(v.picklist(["active", "suspended"], invalid)),
});
export const AdminLiveQuerySchema = v.object({
  ...v.pick(AdminAccountsQuerySchema, ["page", "perPage", "order", "q"])
    .entries,
  sort: v.optional(v.literal("created_at", invalid), "created_at"),
});
// What the browser reports to Workers Logs: no names, messages or ids.
const count = (max: number) =>
  v.pipe(v.number(), v.integer(), v.minValue(0), v.maxValue(max));
export const TelemetrySchema = v.variant("kind", [
  v.object({
    kind: v.literal("error"),
    message: v.pipe(v.string(), v.maxLength(300)),
    source: v.optional(v.pipe(v.string(), v.maxLength(200))),
  }),
  v.object({
    kind: v.literal("call"),
    media: CallMediaSchema,
    connected: v.boolean(),
    seconds: count(86400),
    // The selected local candidate: relay means the call went through TURN.
    transport: v.optional(v.picklist(["host", "srflx", "prflx", "relay"])),
    reconnects: count(100),
  }),
]);
export const OkResponseSchema = v.object({ ok: v.literal(true) });
export const ErrorResponseSchema = v.object({
  error: v.pipe(v.string(), v.minLength(1)),
});
export const PeopleResponseSchema = v.object({ people: v.array(PersonSchema) });
export const MessagesResponseSchema = v.object({
  messages: v.array(ChatMessageSchema),
  hasOlder: v.boolean(),
});
export const MessageResponseSchema = v.object({ message: ChatMessageSchema });
export const CallResponseSchema = v.object({
  call: CallViewSchema,
  signals: v.array(IceSignalSchema),
});
export const StartCallResponseSchema = v.object({ id: IdSchema });
export const CallLinkResponseSchema = v.object({ link: CallLinkSchema });
// What a visitor sees before joining: the host's public profile and presence.
export const CallInviteResponseSchema = v.object({
  host: PersonSchema,
  expires_at: v.number(),
});
// Invitations remain readable after their group link ends.
export const GroupInvitationsResponseSchema = v.object({
  invitations: v.array(GroupInvitationSchema),
});
// What a visitor sees before joining a group call.
export const GroupInviteResponseSchema = v.object({
  organizer: PersonSchema,
  people: v.number(),
  admission: v.boolean(),
  request: v.picklist(["none", "waiting", "approved", "joined", "rejected"]),
  waiting: v.array(v.object({ id: IdSchema, name: v.string() })),
  blocked: v.array(v.object({ id: IdSchema, name: v.string() })),
});
// Where to connect and a token for that one room, used once and never kept.
export const GroupCallResponseSchema = v.object({
  kind: v.literal("joined"),
  link: GroupLinkSchema,
  url: v.string(),
  token: v.string(),
  organizer: IdSchema,
});
export type GroupCallEntry = v.InferOutput<typeof GroupCallResponseSchema>;
// A visitor who joined by name also gets the account that made for them.
export const GroupJoinResponseSchema = v.variant("kind", [
  v.object({
    ...GroupCallResponseSchema.entries,
    session: v.optional(AuthenticatedSnapshotSchema),
  }),
  v.object({
    kind: v.literal("waiting"),
    link: GroupLinkSchema,
    session: v.optional(AuthenticatedSnapshotSchema),
  }),
]);
export type GroupJoinResult =
  | Omit<GroupCallEntry, "link">
  | Pick<
      Extract<
        v.InferOutput<typeof GroupJoinResponseSchema>,
        { kind: "waiting" }
      >,
      "kind"
    >;
export const PersonResponseSchema = v.object({ person: PersonSchema });
export const ProfileLinkResponseSchema = v.object({ code: ProfileCodeSchema });
// The VAPID key, null when notifications are not configured, and what this
// session saved for its device.
export const PushResponseSchema = v.object({
  publicKey: v.nullable(v.string()),
  subscription: v.nullable(
    v.object({ endpoint: v.string(), lang: LocaleSchema }),
  ),
});
export const AdminSessionResponseSchema = v.object({
  id: IdSchema,
  name: v.string(),
});
// Totals and per-day counts only, for the panel's landing page. Days are UTC,
// each given as its first millisecond, oldest first.
export const AdminOverviewSchema = v.object({
  open_reports: v.number(),
  oldest_report_at: v.nullable(v.number()),
  live_calls: v.number(),
  ringing_calls: v.number(),
  guest_passes: v.number(),
  call_links: v.number(),
  accounts: v.number(),
  suspended_accounts: v.number(),
  days: v.array(
    v.object({
      day: v.number(),
      accounts: v.number(),
      calls: v.number(),
      answered: v.number(),
    }),
  ),
});
export type AdminOverview = v.InferOutput<typeof AdminOverviewSchema>;
export const AdminAccountsResponseSchema = v.object({
  data: v.array(AdminAccountSchema),
  total: v.number(),
});
export const AdminAccountResponseSchema = v.object({
  data: AdminAccountSchema,
});
export const AdminCallsResponseSchema = v.object({
  data: v.array(AdminCallSchema),
  total: v.number(),
});
export const AdminCallResponseSchema = v.object({ data: AdminCallSchema });
export const AdminGuestPassesResponseSchema = v.object({
  data: v.array(AdminGuestPassSchema),
  total: v.number(),
});
export const AdminGuestPassResponseSchema = v.object({
  data: AdminGuestPassSchema,
});
export const AdminCallLinksResponseSchema = v.object({
  data: v.array(AdminCallLinkSchema),
  total: v.number(),
});
export const AdminCallLinkResponseSchema = v.object({
  data: AdminCallLinkSchema,
});
export const RecoveryResponseSchema = v.object({
  code: v.pipe(v.string(), v.regex(sessionToken)),
});
export const DeviceSignInStartResponseSchema = v.object({
  code: v.string(),
  token: v.pipe(v.string(), v.regex(sessionToken)),
  expiresAt: v.number(),
});
export const DeviceSignInCheckResponseSchema = v.variant("authenticated", [
  AuthenticatedSnapshotSchema,
  v.object({
    authenticated: v.literal(false),
    state: v.picklist(["waiting", "gone"]),
  }),
]);
export const SessionsResponseSchema = v.object({
  sessions: v.array(
    v.object({
      id: v.string(),
      device: v.string(),
      place: v.nullable(v.string()),
      renewed_at: v.number(),
      current: v.boolean(),
    }),
  ),
});
export const DeviceSignInReviewResponseSchema = v.object({
  device: v.string(),
  place: v.nullable(v.string()),
  created_at: v.number(),
});
// The library owns these shapes, so only what a ceremony cannot start without
// is checked here.
const ceremonyOptions = <T>() =>
  v.custom<T>((input) => v.is(v.looseObject({ challenge: v.string() }), input));
export const PasskeyCreationOptionsSchema =
  ceremonyOptions<PublicKeyCredentialCreationOptionsJSON>();
export const PasskeyRequestOptionsSchema =
  ceremonyOptions<PublicKeyCredentialRequestOptionsJSON>();

export type ApiRequest = v.InferInput<typeof ApiRequestSchema>;
export type Telemetry = v.InferInput<typeof TelemetrySchema>;
export type ConnectionAction = v.InferOutput<typeof ConnectionActionSchema>;
export type PushState = v.InferOutput<typeof PushResponseSchema>;

const adminPage = v.pick(AdminAccountsQuerySchema, [
  "page",
  "perPage",
  "q",
]).entries;
export const AdminReportsQuerySchema = v.object({
  ...adminPage,
  sort: v.optional(v.picklist(["created_at", "status"], invalid), "created_at"),
  order: v.optional(v.picklist(["ASC", "DESC"], invalid), "ASC"),
  status: v.optional(v.picklist(ReportStatusSchema.options, invalid)),
  target: v.optional(text(80)),
  reporter: v.optional(text(80)),
});
export const AdminAuditQuerySchema = v.object({
  ...adminPage,
  sort: v.optional(v.literal("created_at", invalid), "created_at"),
  order: v.optional(v.picklist(["ASC", "DESC"], invalid), "DESC"),
  action: v.optional(v.picklist(moderationActions, invalid)),
  target: v.optional(text(80)),
  report_id: v.optional(text(80)),
});
export const AdminReportsResponseSchema = v.object({
  data: v.array(AdminReportSchema),
  total: v.number(),
});
export const AdminReportResponseSchema = v.object({ data: AdminReportSchema });
export const AdminAuditResponseSchema = v.object({
  data: v.array(AdminAuditSchema),
  total: v.number(),
});
export const AdminAuditRecordResponseSchema = v.object({
  data: AdminAuditSchema,
});
